/**
 * «Распределение» → «Сделки»: сколько сделок обзвона на ком сейчас и кто сколько
 * обзвонил за сегодня. Спека `docs/specs/2026-09-25-deal-distribution-stats-design.md`;
 * числа — те же, что у `prokat-runner deals-stats`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { type DealStatsCell, type DealStatsToday, loadDealStats } from "@/lib/distribution";
import { shortTime } from "@/lib/leads";

/** Цифра «сейчас распределено» → те же сделки списком на «Обзвоне» (`?user=&type=`; `user=0` — свободные). */
export function DialingLink({ n, user, type }: { n: number; user?: number; type?: number }) {
  if (!n) return <span className="text-fg-faint">·</span>;
  const q = new URLSearchParams();
  if (user !== undefined) q.set("user", String(user));
  if (type !== undefined) q.set("type", String(type));
  return (
    <Link to={`/dialing?${q}`} className="underline decoration-border underline-offset-2 hover:decoration-fg" title="Открыть сделки">
      {n}
    </Link>
  );
}

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "whitespace-nowrap px-3 py-1.5 text-right font-medium";
const td = "px-3 py-1.5 text-right tabular-nums";

function Cell({ c }: { c: DealStatsCell | undefined }) {
  if (!c || c.called === 0) return <span className="text-fg-faint">·</span>;
  return (
    <>
      {c.called}
      <span className="text-fg-faint"> / </span>
      <span className="text-fg-muted">{c.closed ?? "—"}</span>
    </>
  );
}

function TodayRow({ u, types, total }: { u: DealStatsToday; types: { id: number }[]; total?: boolean }) {
  return (
    <tr
      className={`border-t border-border ${total ? "font-medium" : "hover:bg-bg-hover"}`}
      data-annot={total ? "обзвонил итого" : `обзвонил ${u.crm_user_id}`}
    >
      <td className="whitespace-nowrap px-4 py-1.5 text-left">{u.name}</td>
      {types.map((t) => (
        <td key={t.id} className={td}>
          <Cell c={u.cells[t.id]} />
        </td>
      ))}
      <td className={`${td} border-l border-border`}>
        <Cell c={u} />
      </td>
      <td className={td}>{u.calls}</td>
      <td className={`${td} text-fg-muted`}>{u.talks}</td>
    </tr>
  );
}

export function DealStatsBlocks() {
  const q = useQuery({ queryKey: ["deal-stats"], queryFn: loadDealStats, refetchInterval: 120_000 });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Считаю статистику обзвона…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить статистику: {q.error.message}</div>;
  const s = q.data;
  const a = s.assigned;
  const queueTypes = s.types.filter((t) => t.id !== 0);
  return (
    <>
      {a && (
        <section className={card} data-annot="сейчас распределено">
          <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
            <h2 className="text-[13px] font-medium">
              Сейчас распределено: {a.total.assigned} из {a.total.total}
            </h2>
            <span className="text-[12px] text-fg-faint">
              все сделки обзвона этих типов, у кого бы ни были · свободно {a.total.free}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[11.5px] text-fg-faint">
                <tr>
                  <th className="px-4 py-1.5 text-left font-medium" />
                  {queueTypes.map((t) => (
                    <th key={t.id} className={th}>
                      {t.name}
                    </th>
                  ))}
                  <th className={`${th} border-l border-border`}>Итого</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border text-fg-muted" data-annot="в обзвоне всего">
                  <td className="px-4 py-1.5">В обзвоне</td>
                  {a.types.map((t) => (
                    <td key={t.type_id} className={td}>
                      {t.total}
                    </td>
                  ))}
                  <td className={`${td} border-l border-border`}>{a.total.total}</td>
                </tr>
                <tr className="border-t border-border font-medium" data-annot="на ком-то">
                  <td className="px-4 py-1.5">Распределено</td>
                  {a.types.map((t) => (
                    <td key={t.type_id} className={td}>
                      {t.assigned}
                    </td>
                  ))}
                  <td className={`${td} border-l border-border`}>{a.total.assigned}</td>
                </tr>
                <tr className="border-t border-border text-fg-muted" data-annot="свободно">
                  <td className="px-4 py-1.5">Свободно</td>
                  {a.types.map((t) => (
                    <td key={t.type_id} className={td}>
                      <DialingLink n={t.free} user={0} type={t.type_id} />
                    </td>
                  ))}
                  <td className={`${td} border-l border-border`}>{a.total.free}</td>
                </tr>
                {a.users.map((u) => (
                  <tr key={u.crm_user_id} className="border-t border-border hover:bg-bg-hover" data-annot={`на ком ${u.crm_user_id}`}>
                    <td className="whitespace-nowrap px-4 py-1.5">{u.name}</td>
                    {queueTypes.map((t) => (
                      <td key={t.id} className={td}>
                        <DialingLink n={u.cells[t.id] ?? 0} user={u.crm_user_id} type={t.id} />
                      </td>
                    ))}
                    <td className={`${td} border-l border-border font-medium`}>{u.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section className={card} data-annot="обзвонил сегодня">
        <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
          <h2 className="text-[13px] font-medium">Сегодня: обзвонил / закрыто</h2>
          <span className="text-[12px] text-fg-faint">
            обзвонил — разных сделок с исходящим звонком, даже без дозвона · закрыто — из них ушли из обзвона · тип — по
            утреннему снимку{s.morning ? ` ${shortTime(s.morning)}` : ""}
          </span>
        </div>
        {!s.morning && (
          <div className="px-4 pt-2 text-[12px] text-warn">
            Утреннего снимка за день нет — тип берём из текущего обзвона, «закрыто» не считаем.
          </div>
        )}
        {s.today.users.length === 0 ? (
          <div className="p-4 text-[12.5px] text-fg-muted">Сегодня исходящих по сделкам ещё не было.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[11.5px] text-fg-faint">
                <tr>
                  <th className="px-4 py-1.5 text-left font-medium">Сотрудник</th>
                  {s.types.map((t) => (
                    <th key={t.id} className={th}>
                      {t.name}
                    </th>
                  ))}
                  <th className={`${th} border-l border-border`}>Итого</th>
                  <th className={th}>Звонков</th>
                  <th className={th}>Разговоров</th>
                </tr>
              </thead>
              <tbody>
                {s.today.users.map((u) => (
                  <TodayRow key={u.crm_user_id} u={u} types={s.types} />
                ))}
                <TodayRow u={s.today.total} types={s.types} total />
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
